import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Captain } from '@sorare/core/src/components/so5/Captain';
import { isType } from '@sorare/core/src/lib/gql';

import { rgbColorPerBonusRating } from 'lib/bonus';

import { BonusOrPointsChip } from './BonusOrPointsChip';
import type { AppearanceAvatar_so5Appearance } from './__generated__/index.graphql';

const StyledVertical = styled(Vertical)`
  align-items: center;
`;
const AvatarWrapper = styled.div`
  position: relative;
  border-radius: var(--unit);
  border: 2px solid transparent;

  &.selected {
    border-color: var(--c-blue-400);
  }
`;
const CaptainWrapper = styled.div`
  position: absolute;
  top: calc(-1 * var(--unit));
  left: calc(-1 * var(--unit));
`;
const Avatar = styled.img`
  width: 100%;
  aspect-ratio: 1;
  border-radius: var(--unit);
  object-fit: cover;
`;

type Props = {
  so5Appearance: AppearanceAvatar_so5Appearance;
  selected: boolean;
  onClick: () => void;
};

export const AppearanceAvatar = ({
  so5Appearance,
  selected,
  onClick,
}: Props) => {
  const {
    avatarUrl,
    captain,
    powerBreakdownOrPowerPointsBreakdown,
    bonusRating,
  } = so5Appearance;

  if (!bonusRating) {
    return null;
  }

  return (
    <ButtonBase onClick={onClick}>
      <StyledVertical>
        <AvatarWrapper className={classNames({ selected, captain })}>
          {!!so5Appearance.captain && (
            <CaptainWrapper>
              <Captain active size={2} />
            </CaptainWrapper>
          )}
          {!!avatarUrl && <Avatar src={avatarUrl} alt="" />}
        </AvatarWrapper>
        <BonusOrPointsChip
          rgbColor={rgbColorPerBonusRating[bonusRating]}
          hasUpcomingGame={!!so5Appearance.upcomingGame}
          bonusBasisPoints={
            isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
              ? powerBreakdownOrPowerPointsBreakdown.collectionBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.seasonBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.xpBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.captainBasisPoints +
                powerBreakdownOrPowerPointsBreakdown.scarcityBasisPoints
              : null
          }
          points={
            isType(powerBreakdownOrPowerPointsBreakdown, 'PowerPointsBreakdown')
              ? powerBreakdownOrPowerPointsBreakdown.collectionPoints +
                powerBreakdownOrPowerPointsBreakdown.seasonPoints +
                powerBreakdownOrPowerPointsBreakdown.xpPoints +
                powerBreakdownOrPowerPointsBreakdown.captainPoints +
                powerBreakdownOrPowerPointsBreakdown.scarcityPoints
              : null
          }
        />
      </StyledVertical>
    </ButtonBase>
  );
};

AppearanceAvatar.fragments = {
  so5Appearance: gql`
    fragment AppearanceAvatar_so5Appearance on So5Appearance {
      id
      bonusRating
      captain
      upcomingGame {
        id
      }
      avatarUrl: pictureUrl(derivative: "avatar")
      powerBreakdownOrPowerPointsBreakdown {
        ... on PowerBreakdown {
          seasonBasisPoints
          collectionBasisPoints
          xpBasisPoints
          captainBasisPoints
          scarcityBasisPoints
        }
        ... on PowerPointsBreakdown {
          collectionPoints
          seasonPoints
          xpPoints
          captainPoints
          scarcityPoints
        }
      }
    }
  ` as TypedDocumentNode<AppearanceAvatar_so5Appearance>,
};
